/**
 * ============================================================================
 * ETRUSTED WIDGET SHADOW DOM STYLE INJECTION
 * ============================================================================
 * 
 * Dieses Modul injiziert Custom CSS in den Shadow DOM des eTrusted Widgets,
 * um das Styling der .wrapper Klasse anzupassen.
 * 
 * ============================================================================
 */

class ETrustedWidgetStyler {
  constructor() {
    this.widgetSelector = 'etrusted-widget';
    this.maxRetries = 10;
    this.retryDelay = 500;
    this.init();
  }

  /**
   * Initialize the widget styler
   */
  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.injectStyles());
    } else {
      this.injectStyles();
    }

    // Also observe for dynamically added widgets
    this.observeWidgets();
  }

  /**
   * Inject custom styles into the shadow DOM
   */
  async injectStyles() {
    const widgets = document.querySelectorAll(this.widgetSelector);

    if (widgets.length === 0) {
      console.log('eTrusted: No widgets found yet, will retry...');
      this.retryInjection();
      return;
    }

    widgets.forEach(widget => this.processWidget(widget));
  }

  /**
   * Process a single widget
   * @param {HTMLElement} widget - The widget element
   */
  async processWidget(widget) {
    let retries = 0;

    const attemptInjection = () => {
      // Check if widget has shadow root
      const shadowRoot = widget.shadowRoot;

      if (!shadowRoot) {
        retries++;
        if (retries < this.maxRetries) {
          console.log(`eTrusted: Shadow root not ready, retry ${retries}/${this.maxRetries}`);
          setTimeout(attemptInjection, this.retryDelay);
        } else {
          console.warn('eTrusted: Shadow root not found after max retries');
        }
        return;
      }

      // Check if wrapper exists
      const wrapper = shadowRoot.querySelector('.wrapper');

      if (!wrapper) {
        retries++;
        if (retries < this.maxRetries) {
          console.log(`eTrusted: .wrapper not found, retry ${retries}/${this.maxRetries}`);
          setTimeout(attemptInjection, this.retryDelay);
        } else {
          console.warn('eTrusted: .wrapper not found after max retries');
        }
        return;
      }

      // Inject the custom styles
      this.injectCustomStyles(shadowRoot);
      console.log('eTrusted: Custom styles successfully injected');
    };

    attemptInjection();
  }

  /**
   * Inject custom CSS into shadow root
   * @param {ShadowRoot} shadowRoot - The shadow root to inject styles into
   */
  injectCustomStyles(shadowRoot) {
    // Check if styles already injected
    if (shadowRoot.querySelector('#custom-etrusted-styles')) {
      console.log('eTrusted: Styles already injected');
      return;
    }

    // Create style element
    const style = document.createElement('style');
    style.id = 'custom-etrusted-styles';
    style.textContent = `
      .wrapper {
        justify-content: flex-start !important;
        margin: 0px !important;
      }
    `;

    // Inject at the beginning of shadow root to allow override
    shadowRoot.insertBefore(style, shadowRoot.firstChild);
  }

  /**
   * Retry injection after delay
   */
  retryInjection() {
    setTimeout(() => this.injectStyles(), this.retryDelay);
  }

  /**
   * Observe for dynamically added widgets
   */
  observeWidgets() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Check if the added node is a widget
            if (node.matches && node.matches(this.widgetSelector)) {
              this.processWidget(node);
            }

            // Check if the added node contains widgets
            const widgets = node.querySelectorAll?.(this.widgetSelector);
            if (widgets && widgets.length > 0) {
              widgets.forEach(widget => this.processWidget(widget));
            }
          }
        });
      });
    });

    // Start observing
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
}

// Initialize when module loads
if (typeof window !== 'undefined') {
  new ETrustedWidgetStyler();
}

export default ETrustedWidgetStyler;

